import React, { useCallback } from "react";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import { Button, ButtonSize } from "@amzn/stencil-react-components/button";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { ManageApplicationCardHeader } from "./card-header";
import { H1 } from "../typography";
import { useHistory } from "react-router-dom";

export interface CardDetail {
  errorCode?: number;
  jobTitle: string;
  hours: string;
  startDate: string;
  startTime: string;
  description: string;
  payRate: string;
  address: string;
  image: string;
  startDateStatus: string;
}

export interface SFAppStep {
  sfApplicationStepSubStep: string;
}

export interface JobScheduleDetails {
  error?: Error;
  scheduleId: string;
  geoClusterId: string;
  externalJobTitle: string;
  scheduleText: string;
  firstDayOnSite: string;
  startTime: string;
  briefJobDescription: string;
  basePay: number;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  image: string;
  shiftDifferential: number;
}

export interface Error {
  errorStatusCode: string;
  errorMessage: string;
  errorCode: string;
}

interface ManageApplicationCardControlledProps {
  details?: CardDetail;
  loading?: boolean;
  error?: boolean;
  onRetry: () => void;
  eligibleForCSS: boolean;
  scheduleIdFound: boolean;
  cssURLWithScheduleId: string;
  cssURLWithoutScheduleId: string;
  onChangeSchedule: () => void;
}

export const CSSSteps = [
  "Pre-Hire:BGC DT Complete Reschedule",
  "Pre-Hire:Schedule Appointment 4",
  "Pre-Hire:PeopleSoft: Manage Hire",
  "Pre-Hire:Error: Initiate Onboarding",
];

export const ManageApplicationCardControlled = ({
  details,
  onRetry,
  loading,
  error,
  eligibleForCSS,
  scheduleIdFound,
  cssURLWithScheduleId,
  cssURLWithoutScheduleId,
  onChangeSchedule,
}: ManageApplicationCardControlledProps) => {
  if (loading) {
    return (
      <Spinner
        size={SpinnerSize.Small}
        loadingText="Loading..."
        showText={true}
      />
    );
  }

  const history = useHistory();
  const navigateToCSSUrl = useCallback(async () => {
    await onChangeSchedule();
    const url = scheduleIdFound
      ? cssURLWithScheduleId
      : cssURLWithoutScheduleId;
    url.startsWith("http") ? window.location.assign(url) : history.push(url);
  }, [history]);

  if (error) {
    return null; // Remove and customize the error state if needed
    return (
      <Col gridGap="1rem">
        <MessageBanner type={MessageBannerType.Error}>
          Unable to retrieve the application details. Please try again later by
          clicking the “Retry” button below.
        </MessageBanner>
        <Row>
          <Button size={ButtonSize.Small} onClick={onRetry}>
            Retry
          </Button>
        </Row>
      </Col>
    );
  }

  if (!scheduleIdFound || !details) {
    return null;
  }

  return (
    <>
      <Col gridGap="1rem">
        <Row alignItems="center">
          <Col>
            <H1>Job details</H1>
          </Col>
        </Row>
        <ManageApplicationCardHeader
          jobTitle={details.jobTitle}
          hours={details.hours}
          startDate={details.startDate}
          startTime={details.startTime}
          description={details.description}
          payRate={details.payRate}
          address={details.address}
          image={details.image}
          startDateStatus={details.startDateStatus}
          onChangeScheduleClick={navigateToCSSUrl}
          eligibleForCSS={eligibleForCSS}
        />
      </Col>
    </>
  );
};
